import { useMemo, useEffect } from 'react';
import { 
  calcularPlanosDisponiveis,
  calcularTipoFluxosDisponiveis,
  calcularTipoCorrecoesDisponiveis
} from '../../../services/simuladorFlex';

/**
 * Extrai e normaliza regrasExtra para sempre retornar um array
 * @param {Array|number} regrasExtra - Array de regras extras ou valor legado único
 * @returns {Array<number>} Array de valores de regras extras
 */
const normalizarRegrasExtra = (regrasExtra) => {
  if (Array.isArray(regrasExtra)) {
    return regrasExtra;
  }
  return [];
};

/**
 * Hook para calcular planos, tipos de fluxo e tipos de correção disponíveis
 * @param {Object} params - Parâmetros necessários para os cálculos
 * @param {Object} params.propertySelection - Dados de seleção de propriedade
 * @param {string} params.selectedPlano - Plano selecionado
 * @param {Object} params.formData - Dados do formulário (incluindo eSAD)
 * @param {Function} params.setFormData - Função para atualizar formData
 * @returns {Object} Planos e tipos disponíveis
 */
export const usePlanosCalculation = ({ propertySelection, selectedPlano, formData, setFormData }) => {
  // Calcular planos disponíveis dinamicamente baseado na unidade selecionada
  const planosDisponiveis = useMemo(() => {
    const unidadeData = propertySelection.selectedUnidadeData;
    const empreendimentoData = propertySelection.selectedEmpreendimentoData;
    
    if (!unidadeData) {
      return []; // Sem unidade selecionada, sem planos
    }

    const parcelasPsaRegra = unidadeData.prosolutoA ?? 0;
    const linhaProduto = empreendimentoData?.padronizacao?.nome || '';
    
    return calcularPlanosDisponiveis(
      parcelasPsaRegra,
      linhaProduto,
      propertySelection.isRendaExterior
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    propertySelection.selectedUnidadeData,
    propertySelection.selectedEmpreendimentoData,
    propertySelection.isRendaExterior
  ]);

  // Calcular tipos de fluxo disponíveis baseado no plano selecionado
  const tiposFluxoDisponiveis = useMemo(() => {
    if (!selectedPlano) {
      return []; // Sem plano selecionado, sem tipos de fluxo
    }

    // selectedPlano é o índice no array planosDisponiveis
    const planoSelecionado = planosDisponiveis[selectedPlano];
    
    if (!planoSelecionado) {
      return [];
    }

    // Parâmetros adicionais para regras complexas futuras
    // Normalizar regrasExtra para sempre ser um array (novo formato) ou converter do legado
    const unidadeData = propertySelection.selectedUnidadeData;
    const regrasExtraData = unidadeData?.empreendimento?.regrasExtra || unidadeData?.empreendimento?.regraExtra;
    const regrasExtraArray = normalizarRegrasExtra(regrasExtraData);
    
    
    const additionalParams = {
      unidadeData: propertySelection.selectedUnidadeData,
      empreendimentoData: propertySelection.selectedEmpreendimentoData,
      isRendaExterior: propertySelection.isRendaExterior,
      eSAD: formData?.eSAD || false,
      regrasExtra: regrasExtraArray
    };

    return calcularTipoFluxosDisponiveis(planoSelecionado, additionalParams);
  }, [selectedPlano, planosDisponiveis, propertySelection.selectedUnidadeData, propertySelection.selectedEmpreendimentoData, propertySelection.isRendaExterior, formData?.eSAD]);

  // Calcular tipos de correção disponíveis baseado no plano selecionado
  const tiposCorrecaoDisponiveis = useMemo(() => {
    if (!selectedPlano) {
      return []; // Sem plano selecionado, sem tipos de correção
    }

    // selectedPlano é o índice no array planosDisponiveis
    const planoSelecionado = planosDisponiveis[selectedPlano];
    
    if (!planoSelecionado) {
      return [];
    }

    // Parâmetros adicionais para regras complexas
    // Normalizar regrasExtra para sempre ser um array (novo formato) ou converter do legado
    const unidadeData = propertySelection.selectedUnidadeData;
    const regrasExtraData = unidadeData?.empreendimento?.regrasExtra || unidadeData?.empreendimento?.regraExtra;
    const regrasExtraArray = normalizarRegrasExtra(regrasExtraData);
    const additionalParams = {
      unidadeData: propertySelection.selectedUnidadeData,
      empreendimentoData: propertySelection.selectedEmpreendimentoData,
      isRendaExterior: propertySelection.isRendaExterior,
      quantidadeParcelas: formData?.quantidadeParcelas,
      eSAD: formData?.eSAD || false,
      regrasExtra: regrasExtraArray
    };

    return calcularTipoCorrecoesDisponiveis(planoSelecionado, additionalParams);
  }, [selectedPlano, planosDisponiveis, propertySelection.selectedUnidadeData, propertySelection.selectedEmpreendimentoData, propertySelection.isRendaExterior, formData?.quantidadeParcelas, formData?.eSAD]);
 
  // Reset tipoFluxo quando o plano muda e o tipo atual não está disponível
  useEffect(() => {
    if (selectedPlano && tiposFluxoDisponiveis.length > 0) {
      setFormData(prev => {
        if (!tiposFluxoDisponiveis.includes(prev.tipoFluxo)) {
          console.log('[TipoFluxo] Auto-selecionado:', tiposFluxoDisponiveis[0]);
          return { ...prev, tipoFluxo: tiposFluxoDisponiveis[0] };
        }
        return prev;
      });
    }
  }, [selectedPlano, tiposFluxoDisponiveis, setFormData]);

  // Reset tipoCorrecao quando o plano muda e o tipo atual não está disponível
  useEffect(() => {
    if (selectedPlano && tiposCorrecaoDisponiveis.length > 0) {
      setFormData(prev => {
        const includesCurrentType = tiposCorrecaoDisponiveis.includes(prev.tipoCorrecao);
        if (!includesCurrentType) {
          return { ...prev, tipoCorrecao: tiposCorrecaoDisponiveis[0] };
        }
        return prev;
      });
    }
  }, [selectedPlano, tiposCorrecaoDisponiveis, setFormData]);

  return {
    planosDisponiveis,
    tiposFluxoDisponiveis,
    tiposCorrecaoDisponiveis
  };
};
